import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, attempt to restore the session via the httpOnly cookie.
  // Skip on public routes (no admin session needed there) to avoid the
  // noisy 401 in the console + one wasted round-trip on mobile.
  useEffect(() => {
    let cancelled = false;
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isPublic =
      path.startsWith("/alta/") ||
      path.startsWith("/firmar/") ||
      path.startsWith("/firmar-dgt/") ||
      path.startsWith("/kai/") ||
      path.startsWith("/horarios/") ||
      path.startsWith("/documentos/") ||
      path.startsWith("/psicotecnico/") ||
      path.startsWith("/confirm");
    if (isPublic) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (err) {
        // 401 here just means "no active session" — expected on first visit.
        if (err?.response?.status !== 401) {
          console.warn("Auth init failed:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password, options = {}) => {
    try {
      const payload = { email, password };
      // V1.37 · Adjuntamos el token de reCAPTCHA v3 si se proporcionó
      if (options && options.recaptchaToken) {
        payload.recaptcha_token = options.recaptchaToken;
      }
      const { data } = await api.post("/auth/login", payload);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      console.error("Login failed:", e);
      return {
        ok: false,
        error: formatApiError(e?.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed:", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
